import { Action, createReducer, on } from '@ngrx/store';
import { createProfileReportFinished } from '@capital-access/common/actions';
import { LoadStatus } from '@capital-access/common/utils';
import {
  ActivityHistoryCount,
  createBnyManagementReport,
  createBnyManagementReportFinished,
  createConfigurableItineraryReport,
  createConfigurableItineraryReportFinished,
  createConfigurableProfileReport,
  loadSelectedItemsFromStorage,
  loadSelectedTimeFromStorage,
  ReportSectionConfigForDraggableItem,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationWithOrder,
  resetZoomValue,
  setZoomValue,
  TemplateDesignerState,
  TemplateExportInitialState,
  TemplateExportListItemWithComments,
  TemplateExportState
} from '@capital-access/reporting/common';
import { getTemplateDesignerSelectedReportState } from '../services/configuration-panel.service';
import { ConfigurationPanelServiceResolver } from '../utils/configuration-panel-service.resolver';
import { getModifiedReportSection, getReportSectionConfig } from '../utils/template-designer.utils';
import {
  clearErrorMessage,
  clearTemplateDesignerState,
  getTemplateDetailsById,
  getTemplateDetailsByIdFailed,
  goBackToReportConfiguration,
  initialUpdateReportSections,
  onEmptySelectionAlert,
  onHideExportWarning,
  onHideSelectWarning,
  onReportSectionCanvasChanged,
  onReportSectionCheckboxToggle,
  onReportSectionConfigAdditionalDataChanged,
  onReportSectionConfigCheckboxToggleFromDetailsPage,
  onReportSectionConfigDataPointToggle,
  onReportSectionDataPointSelectClearAll,
  onReportSectionOrderChanged,
  onReportSectionWidthChanged,
  reportSectionClearAll,
  reportSectionSelectAll,
  reportSectionSelected,
  reportSectionSelectedViaCanvas,
  saveTemplateDetails,
  saveTemplateDetailsSuccess,
  setCombinedOwnershipEnabled,
  setNotesFeatureEnabled,
  toggleSelectionByParticipant,
  updateActivityHistoryCount,
  updateReportData,
  changeItineraryTab
} from './actions';
import {
  addExportItemToList,
  addRemoveItineraryToList,
  changeItinerarySelectedItem,
  changeSelectedItem,
  clearExportState,
  filterActivityApplySuccess,
  formatListForTemplateExportSuccess,
  getListItemsBySelectedListId,
  onCommentsSaveSuccess,
  onLoadCompanyContacts,
  onLoadCompanyContactSuccess,
  openCommentSectionDrawerForSelectedItem,
  openExportListDrawer,
  openFilterActivityDrawer,
  removeExportItemFromList,
  setExportList,
  setItineraryComments,
  setSelectedBnyDate,
  setSelectedBnySecurityId,
  setTemplateExportData
} from './export/export-actions';
export const TemplateZoomInitialState = {
  zoom: 1,
  previousZoom: null
};
export const initialState: TemplateDesignerState = {
  reportTemplate: null,
  reportSections: [],
  initialReportSections: [],
  formattedSections: [],
  reportData: {},
  sampleData: true,
  loadingState: LoadStatus.Pristine,
  savingState: LoadStatus.Pristine,
  selectedReport: null,
  error: null,
  activityHistoryCount: ActivityHistoryCount.Five,
  isSelectionByParticipant: false,
  export: TemplateExportInitialState,
  showEmptySelectionAlert: false,
  showCombinedOwnership: false,
  showNotesFeature: false,
  getDetailsError: null,
  initial: false,
  zoomState: TemplateZoomInitialState,
  selectedItineraryTab: 'events'
};
const reducer = createReducer(
  initialState,
  on(initialUpdateReportSections, (state, { reportTemplate, reportSections, draggableItems, initial }) => ({
    ...state,
    reportTemplate,
    reportSections,
    initialReportSections: reportSections,
    formattedSections: draggableItems,
    loadingState: LoadStatus.Loaded,
    initial: initial
  })),
  on(setZoomValue, (state, { zoom, savePrevious }) => ({
    ...state,
    zoomState: {
      ...state.zoomState,
      previousZoom: savePrevious ? state.zoomState.zoom : state.zoomState.previousZoom,
      zoom
    }
  })),
  on(resetZoomValue, state => ({
    ...state,
    zoomState: {
      ...state.zoomState,
      zoom: state.zoomState.previousZoom ?? state.zoomState.zoom,
      previousZoom: null
    }
  })),
  on(reportSectionSelected, (state, { selectedReport }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    return {
      ...state,
      reportSections: [...state.reportSections].map(section => {
        return {
          ...section,
          isActiveTemplate: section.config.name === selectedReport?.name
        };
      }),
      selectedReport: panelService.FindSelectedReportSectionConfig(
        state.reportSections,
        selectedReport as ReportSectionConfigForDraggableItem
      )
    };
  }),
  on(reportSectionSelectedViaCanvas, (state, { selectedReport }) => ({
    ...state,
    reportSections: [...state.reportSections].map(section => {
      return {
        ...section,
        isActiveTemplate: section.config.name === selectedReport?.config.name
      };
    }),
    selectedReport: getTemplateDesignerSelectedReportState(
      state.reportSections.find(section => section.config.name === selectedReport?.config.name)!
    )
  })),
  on(goBackToReportConfiguration, state => ({
    ...state,
    reportSections: [...state.reportSections].map(section => {
      return {
        ...section,
        isActiveTemplate: false
      };
    }),
    selectedReport: null
  })),
  on(onReportSectionCheckboxToggle, (state, { reportSection, checked }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSections = panelService.ReportSectionCheckboxToggle(
      checked,
      [...state.reportSections],
      reportSection.name
    );

    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections)
    };
  }),
  on(onReportSectionConfigCheckboxToggleFromDetailsPage, (state, { reportSection, checked }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSections = panelService.ReportSectionCheckboxToggle(
      checked,
      [...state.reportSections],
      reportSection.name
    );
    const modifiedReportSectionConfig = getReportSectionConfig(modifiedReportSections, reportSection?.name);
    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections),
      selectedReport: {
        reportSectionConfig: modifiedReportSectionConfig!,
        reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(modifiedReportSectionConfig!)
      }
    };
  }),
  on(clearErrorMessage, state => ({
    ...state,
    error: null
  })),
  on(onHideExportWarning, state => ({
    ...state,
    export: {
      ...state.export,
      showExportWarning: false
    }
  })),
  on(onHideSelectWarning, state => ({
    ...state,
    export: {
      ...state.export,
      showSelectWarning: false
    }
  })),
  on(onReportSectionConfigDataPointToggle, (state, { dataSection, checked }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSection = panelService.UpdateReportSectionConfigOnDataSectionToggle(
      dataSection,
      checked,
      state.selectedReport?.reportSectionConfig as ReportSectionConfigurationWithOrder
    );
    const modifiedReportSections = getModifiedReportSection([...state.reportSections], modifiedReportSection);
    return {
      ...state,
      reportSections: modifiedReportSections,
      selectedReport: {
        reportSectionConfig: modifiedReportSection,
        reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(modifiedReportSection)
      },
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections)
    };
  }),
  on(onReportSectionDataPointSelectClearAll, (state, { selectAll }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSection = panelService.ReportSectionDataPointSelectClearAll(
      selectAll,
      state.selectedReport?.reportSectionConfig as ReportSectionConfigurationWithOrder
    );
    const modifiedReportSections = getModifiedReportSection([...state.reportSections], modifiedReportSection);
    return {
      ...state,
      reportSections: modifiedReportSections,
      selectedReport: {
        reportSectionConfig: modifiedReportSection,
        reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(modifiedReportSection)
      },
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections)
    };
  }),
  on(onReportSectionWidthChanged, (state, { reportSection, width }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const selectedSection = getReportSectionConfig(state.reportSections, reportSection.name)!;
    if (panelService.IsReportSectionConfigWidthValid(width, selectedSection)) {
      const modifiedReportSections = [...state.reportSections].map(section => {
        if (section.config.name === reportSection.name) {
          return { ...section, width: width } as ReportSectionConfigurationWithOrder;
        } else {
          const nestedMatch = section.config.nestedItems?.find(nested => nested.name === reportSection.name);
          if (nestedMatch && nestedMatch.nestedItemSectionConfiguration) {
            // Create a new nestedItemSectionConfiguration with updated width
            const updatedNestedItemSectionConfiguration = {
              ...nestedMatch.nestedItemSectionConfiguration,
              width: width
            };
            // Update the nestedItems array with the modified nested item
            const updatedNestedItems = section.config.nestedItems?.map(nested =>
              nested.name === reportSection.name
                ? { ...nested, nestedItemSectionConfiguration: updatedNestedItemSectionConfiguration }
                : nested
            );
            // Return the updated section with modified nestedItems
            return {
              ...section,
              config: {
                ...section.config,
                nestedItems: updatedNestedItems
              }
            } as ReportSectionConfigurationWithOrder;
          }
        }
        return section as ReportSectionConfigurationWithOrder;
      });
      const updatedSelectedReport =
        state.selectedReport?.reportSectionConfig.config.name === reportSection.name
          ? {
              ...state.selectedReport,
              reportSectionConfig: {
                ...state.selectedReport.reportSectionConfig,
                width: width
              }
            }
          : state.selectedReport;
      return {
        ...state,
        reportSections: modifiedReportSections,
        formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections),
        selectedReport: updatedSelectedReport
      };
    }
    return {
      ...state,
      formattedSections: panelService.ConvertReportSectionsToDraggableList([...state.reportSections]),
      error: panelService.GetWidthErrorMessage(selectedSection)
    };
  }),
  on(onReportSectionOrderChanged, (state, { draggableItems }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSections = panelService.UpdateReportSectionOrder(
      [...draggableItems],
      [...state.reportSections]
    );

    const updatedSelectedReport = state.selectedReport
      ? (() => {
          const updatedSection = modifiedReportSections.find(
            section => section.config.name === state.selectedReport?.reportSectionConfig.config.name
          );
          return updatedSection
            ? {
                reportSectionConfig: updatedSection,
                reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(updatedSection)
              }
            : state.selectedReport;
        })()
      : null;

    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections),
      selectedReport: updatedSelectedReport
    };
  }),
  on(onReportSectionCanvasChanged, (state, { changedReportSections }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const selectedReport = state.selectedReport?.reportSectionConfig.config.name;
    const changedSectionsMap = new Map(
      changedReportSections.map((section, index) => [section.config.name, { ...section, order: index }])
    );
    const sortedChangedSections = [...changedSectionsMap.values()];
    let changedIndex = 0;
    const modifiedReportSections = state.reportSections.map((original, index) => {
      if (changedSectionsMap.has(original.config.name)) {
        return { ...sortedChangedSections[changedIndex++], order: index };
      }
      return original;
    });
    const updatedSelectedReport = modifiedReportSections.find(section => section.config.name === selectedReport);
    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections),
      selectedReport: updatedSelectedReport
        ? {
            reportSectionConfig: updatedSelectedReport,
            reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(updatedSelectedReport)
          }
        : state.selectedReport
    };
  }),
  on(updateActivityHistoryCount, (state, { activityHistoryCount }) => ({
    ...state,
    activityHistoryCount
  })),
  on(updateReportData, (state, { reportData, sampleData }) => ({
    ...state,
    reportData,
    sampleData
  })),
  on(toggleSelectionByParticipant, state => ({
    ...state,
    isSelectionByParticipant: !state.isSelectionByParticipant
  })),
  on(getTemplateDetailsById, state => ({
    ...state,
    loadingState: LoadStatus.Loading
  })),
  on(getTemplateDetailsByIdFailed, (state, { error }) => ({
    ...state,
    loadingState: LoadStatus.Loaded,
    getDetailsError: error
  })),
  on(saveTemplateDetails, state => ({
    ...state,
    savingState: state.initial ? LoadStatus.Loaded : LoadStatus.Loading
  })),
  on(saveTemplateDetailsSuccess, state => ({
    ...state,
    savingState: LoadStatus.Loaded
  })),
  /** Export Actions */
  on(setTemplateExportData, (state, { exportData }) => ({
    ...state,
    export: {
      ...state.export,
      exportData
    } as TemplateExportState
  })),
  on(openExportListDrawer, state => ({
    ...state,
    export: {
      ...state.export,
      addListDrawerOpened: true
    } as TemplateExportState
  })),
  on(clearExportState, state => ({
    ...state,
    export: {
      ...TemplateExportInitialState,
      exportItineraryComments: state.export.exportItineraryComments,
      selectedList: state.export.selectedList
    }
  })),
  on(loadSelectedItemsFromStorage, (state, { selectedItems }) => {
    let exportItineraryComments = state.export.exportItineraryComments;

    if (Array.isArray(selectedItems) && selectedItems.length > 0) {
      const itemWithComments = selectedItems.find(item => item.comment || item.commentTitle);
      if (itemWithComments) {
        exportItineraryComments = [
          {
            comment: itemWithComments.comment || '',
            title: itemWithComments.commentTitle || '',
            profileId: '|'
          }
        ];
      }
    }

    return {
      ...state,
      export: {
        ...state.export,
        selectedList: selectedItems,
        exportItineraryComments
      }
    };
  }),
  on(loadSelectedTimeFromStorage, (state, { selectedTime }) => ({
    ...state,
    export: {
      ...state.export,
      exportBnyReportTime: new Date(selectedTime)
    }
  })),
  on(getListItemsBySelectedListId, state => ({
    ...state,
    export: {
      ...state.export,
      loaderStatus: LoadStatus.Loading
    } as TemplateExportState
  })),
  on(formatListForTemplateExportSuccess, (state, { list }) => {
    const newSelectedList = [...state.export.selectedList, ...list];
    return {
      ...state,
      export: {
        ...state.export,
        loaderStatus: LoadStatus.Loaded,
        selectedList: newSelectedList
      } as TemplateExportState
    };
  }),
  on(addExportItemToList, (state, { itemToAdd }) => {
    const alreadyExists = state.export.selectedList.some(item => {
      // Check by activityId for activities
      if (itemToAdd.activityId) {
        if (item.activityId) {
          return item.activityId === itemToAdd.activityId;
        }
        return false;
      }
      // Check by eventId for events
      if (itemToAdd.eventId) {
        if (item.eventId) {
          return item.eventId === itemToAdd.eventId;
        }
        return false;
      }
      // Check by prId and crmId for contacts/institutions
      if (itemToAdd.prId && itemToAdd.crmId) {
        if (item.prId && item.crmId) {
          return item.prId === itemToAdd.prId && item.crmId === itemToAdd.crmId;
        }
        return false;
      }
      return item.displayName === itemToAdd.displayName;
    });
    if (alreadyExists) {
      return state;
    }
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: [
          ...state.export.selectedList,
          {
            ...itemToAdd,
            index: state.export.selectedList.length + 1
          }
        ]
      }
    };
  }),

  on(addRemoveItineraryToList, (state, { itemsToAdd }) => {
    let updatedList = [...state.export.selectedList];

    // Filter out items from updatedList that don't exist in itemsToAdd
    updatedList = updatedList.filter(existingItem => {
      return itemsToAdd.some(itemToAdd => {
        // Check by activityId for activities
        if (itemToAdd.activityId && existingItem.activityId) {
          return itemToAdd.activityId === existingItem.activityId;
        }
        // Fallback to displayName
        return itemToAdd.displayName === existingItem.displayName;
      });
    });

    // Add new items from itemsToAdd that don't already exist in updatedList
    const itemsToAddFiltered = itemsToAdd.filter(itemToAdd => {
      return !updatedList.some(item => {
        // Check by activityId for activities
        if (itemToAdd.activityId && item.activityId) {
          return itemToAdd.activityId === item.activityId;
        }
        // Fallback to displayName
        return itemToAdd.displayName === item.displayName;
      });
    });

    const newItems = itemsToAddFiltered.map((item, index) => ({
      ...item,
      index: updatedList.length + index + 1
    }));

    updatedList = [...updatedList, ...newItems];

    // Sort by startDate asc
    updatedList.sort((a, b) => {
      const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
      const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
      return dateA - dateB;
    });

    updatedList = updatedList.map((item, index) => ({
      ...item,
      index: index + 1
    }));

    return {
      ...state,
      export: {
        ...state.export,
        selectedList: updatedList
      }
    };
  }),
  on(changeSelectedItem, (state, { index }) => {
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: state.export.selectedList.map((item, i) => {
          return {
            ...item,
            isSelected: i == index,
            index: i + 1
          };
        })
      }
    };
  }),
  on(changeItinerarySelectedItem, (state, { index }) => {
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: state.export.selectedList.map((item, i) => {
          return {
            ...item,
            isSelected: i <= index,
            index: i + 1
          };
        })
      }
    };
  }),
  on(setExportList, (state, { list }) => {
    if (!list || list.length === 0) {
      return {
        ...state,
        export: {
          ...state.export,
          selectedList: []
        }
      };
    }
    const [item] = list;
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: [
          {
            ...item,
            isSelected: true,
            index: 1
          }
        ]
      }
    };
  }),
  on(removeExportItemFromList, (state, { itemToRemove }) => {
    const updatedList = itemToRemove.id
      ? state.export.selectedList.filter(item => item.id !== itemToRemove.id)
      : state.export.selectedList.filter(item => item.crmId !== itemToRemove.crmId);
    const wasItemSelected = itemToRemove.id
      ? state.export.selectedList.some(item => item.id === itemToRemove.id && item.isSelected)
      : state.export.selectedList.some(item => item.crmId === itemToRemove.crmId && item.isSelected);
    const nextSelectedIndex = calculateNextSelectedIndex(
      state.export.selectedList,
      updatedList,
      itemToRemove,
      wasItemSelected
    );
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: updatedList.map((item, index) => ({
          ...item,
          isSelected: index === nextSelectedIndex // Update selection status
        }))
      }
    };
  }),
  /** Comment Section Drawer Actions */
  on(openCommentSectionDrawerForSelectedItem, (state, { selectedItem }) => ({
    ...state,
    export: {
      ...state.export,
      commentSectionDrawerState: {
        isOpened: true,
        selectedListItem: selectedItem
      }
    }
  })),
  on(onCommentsSaveSuccess, (state, { list }) => {
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: [...list],
        commentSectionDrawerState: {
          isOpened: false,
          selectedListItem: null
        }
      }
    };
  }),

  /** filter activities drawer actions */
  on(openFilterActivityDrawer, (state, { selectedItem }) => {
    return {
      ...state,
      export: {
        ...state.export,
        filterActivitiesDrawerState: {
          ...state.export.filterActivitiesDrawerState,
          isOpened: true,
          selectedListItem: selectedItem
        }
      }
    };
  }),
  on(onLoadCompanyContacts, state => ({
    ...state,
    export: {
      ...state.export,
      filterActivitiesDrawerState: {
        ...state.export.filterActivitiesDrawerState,
        companyContactLoadStatus: LoadStatus.Loading
      }
    }
  })),
  on(onLoadCompanyContactSuccess, (state, { list }) => ({
    ...state,
    export: {
      ...state.export,
      filterActivitiesDrawerState: {
        ...state.export.filterActivitiesDrawerState,
        companyContactLoadStatus: LoadStatus.Loaded,
        companyContacts: list
      }
    }
  })),
  on(filterActivityApplySuccess, (state, { list }) => {
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: [...list]
      }
    };
  }),
  on(clearTemplateDesignerState, state => ({
    ...initialState,
    zoomState: state.zoomState
  })),
  on(reportSectionSelectAll, state => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSections = panelService.ReportSectionCheckboxToggle(true, [...state.reportSections], '', true);
    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections)
    };
  }),
  on(reportSectionClearAll, state => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSections = panelService.ReportSectionCheckboxToggle(false, [...state.reportSections], '', true);
    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections)
    };
  }),
  on(onReportSectionConfigAdditionalDataChanged, (state, { dataSection, additionalConfiguration }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSection: ReportSectionConfigurationWithOrder = {
      ...dataSection,
      config: {
        ...dataSection.config,
        additionalConfiguration: {
          ...dataSection.config.additionalConfiguration,
          ...additionalConfiguration
        }
      } as ReportSectionConfigurationBase
    };
    const { minSize, maxSize } = panelService.GetUpdatedSizes(modifiedReportSection);
    modifiedReportSection.config.minSize = minSize;
    modifiedReportSection.config.maxSize = maxSize;
    const modifiedReportSections = [...state.reportSections].map(section => {
      if (section.config.name === modifiedReportSection.config.name) {
        return modifiedReportSection;
      } else {
        // Check if the section has nestedItems and if any nested item's name matches
        if (section.config.nestedItems) {
          const updatedNestedItems = section.config.nestedItems.map(nested => {
            if (nested.name === modifiedReportSection.config.name && nested.nestedItemSectionConfiguration) {
              return {
                ...nested,
                nestedItemSectionConfiguration: modifiedReportSection
              };
            }
            return nested;
          });
          return {
            ...section,
            config: {
              ...section.config,
              nestedItems: updatedNestedItems
            }
          } as ReportSectionConfigurationWithOrder;
        }
      }
      return section;
    });
    return {
      ...state,
      reportSections: modifiedReportSections,
      selectedReport: {
        ...state.selectedReport,
        reportSectionConfig: modifiedReportSection,
        reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(modifiedReportSection)
      }
    };
  }),
  on(onEmptySelectionAlert, (state, { show }) => ({
    ...state,
    showEmptySelectionAlert: show
  })),
  on(setCombinedOwnershipEnabled, (state, { enabled }) => ({
    ...state,
    showCombinedOwnership: enabled
  })),
  on(setNotesFeatureEnabled, (state, { enabled }) => ({
    ...state,
    showNotesFeature: enabled
  })),
  on(createConfigurableProfileReport, createBnyManagementReport, state => ({
    ...state,
    export: {
      ...state.export,
      exportStatus: LoadStatus.Loading
    }
  })),
  on(
    createProfileReportFinished,
    createBnyManagementReportFinished,
    createConfigurableItineraryReportFinished,
    state => ({
      ...state,
      export: {
        ...state.export,
        exportStatus: LoadStatus.Loaded
      }
    })
  ),
  on(createConfigurableItineraryReport, state => ({
    ...state,
    export: {
      ...state.export,
      exportStatus: LoadStatus.Loading
    }
  })),
  on(setSelectedBnyDate, (state, { selectedDate }) => ({
    ...state,
    export: {
      ...state.export,
      exportBnyReportTime: selectedDate
    } as TemplateExportState
  })),
  on(setSelectedBnySecurityId, (state, { selectedSecurityId }) => ({
    ...state,
    export: {
      ...state.export,
      exportBnySecurityId: selectedSecurityId
    } as TemplateExportState
  })),
  on(setItineraryComments, (state, { itineraryComments }) => {
    const updatedList = state.export.selectedList.map(item => {
      if (itineraryComments.length > 0) {
        const comment = itineraryComments[0];
        return {
          ...item,
          comment: comment.comment,
          commentTitle: comment.title
        };
      }
      return item;
    });

    return {
      ...state,
      export: {
        ...state.export,
        exportItineraryComments: itineraryComments,
        selectedList: updatedList
      } as TemplateExportState
    };
  }),
  on(changeItineraryTab, (state, { selectedTab }) => ({
    ...state,
    selectedItineraryTab: selectedTab
  }))
);
export function templateDesignerReducer(state: TemplateDesignerState | undefined, action: Action) {
  return reducer(state, action);
}
export const getInitialState = () => initialState;
// Helper function to calculate the next selected index
function calculateNextSelectedIndex(
  originalList: TemplateExportListItemWithComments[],
  updatedList: TemplateExportListItemWithComments[],
  itemToRemove: TemplateExportListItemWithComments,
  wasItemSelected: boolean
) {
  if (wasItemSelected) {
    if (updatedList.length > 0) {
      // Find the index of the item to remove and ensure it doesn't exceed the updated list length
      return Math.min(
        originalList.findIndex(item => item.id === itemToRemove.id),
        updatedList.length - 1
      );
    }
    return -1; // No items left to select
  }
  // If the item wasn't selected, return the index of the currently selected item
  return updatedList.findIndex(item => item.isSelected);
}
